/**
 * BlockRenderer - Rich Block Engine Component
 *
 * Renders individual blocks based on type.
 * Supports paragraphs, headings, bullets, quotes, code, citations, and more.
 *
 * Extracted for Rich Block Engine integration (Wave 27+).
 *
 * @module components/BlockRenderer
 */

import React, { memo, useMemo } from 'react';

import { FileText, Quote, Code, Link2, Image, List, Hash, Minus } from 'lucide-react';

import { BlockType } from '@/pages/intelligence-studio/hooks/useBlockState';

/**
 * Block Wrapper - Common container for all blocks
 */
const BlockWrapper = memo(function BlockWrapper({
  children,
  block,
  isSelected,
  onSelect,
  className = '',
}) {
  return (
    <div
      data-block-id={block.id}
      data-block-type={block.type}
      className={`
        group relative block-wrapper
        ${isSelected ? 'ring-2 ring-amber-500/50 bg-amber-50/30 dark:bg-amber-900/10' : ''}
        ${block.metadata?.isStreaming ? 'animate-pulse' : ''}
        ${className}
      `}
      onClick={() => onSelect?.(block.id)}
    >
      {children}
    </div>
  );
});

/**
 * Paragraph Block
 */
const ParagraphBlock = memo(function ParagraphBlock({ block, onCitationClick }) {
  return (
    <p className="text-slate-700 dark:text-neutral-300 leading-relaxed whitespace-pre-wrap">
      {block.content}
      {block.metadata?.isStreaming && (
        <span className="inline-block w-2 h-4 bg-amber-500 animate-blink ml-0.5" />
      )}
    </p>
  );
});

/**
 * Heading Block
 */
const HeadingBlock = memo(function HeadingBlock({ block }) {
  const level = block.metadata?.level || 1;
  const Tag = `h${Math.min(level, 6)}`;

  const sizeClasses = {
    1: 'text-2xl font-bold',
    2: 'text-xl font-semibold',
    3: 'text-lg font-semibold',
    4: 'text-base font-medium',
    5: 'text-sm font-medium',
    6: 'text-xs font-medium uppercase tracking-wide',
  };

  return (
    <Tag className={`${sizeClasses[level]} text-slate-900 dark:text-white mt-4 mb-2`}>
      {block.content}
    </Tag>
  );
});

/**
 * Bullet List Block
 */
const BulletBlock = memo(function BulletBlock({ block }) {
  const items = block.content.split('\n').filter(Boolean);

  return (
    <ul className="list-disc list-inside space-y-1 text-slate-700 dark:text-neutral-300">
      {items.map((item, idx) => (
        <li key={idx} className="leading-relaxed">
          {item}
        </li>
      ))}
    </ul>
  );
});

/**
 * Numbered List Block
 */
const NumberedBlock = memo(function NumberedBlock({ block }) {
  const items = block.content.split('\n').filter(Boolean);

  return (
    <ol className="list-decimal list-inside space-y-1 text-slate-700 dark:text-neutral-300">
      {items.map((item, idx) => (
        <li key={idx} className="leading-relaxed">
          {item}
        </li>
      ))}
    </ol>
  );
});

/**
 * Quote Block
 */
const QuoteBlock = memo(function QuoteBlock({ block }) {
  return (
    <blockquote className="border-l-4 border-amber-500 pl-4 py-2 italic text-slate-600 dark:text-neutral-400 bg-slate-50 dark:bg-neutral-800/50 rounded-r-lg">
      <Quote className="h-4 w-4 inline-block mr-2 text-amber-500 opacity-50" />
      {block.content}
      {block.metadata?.source && (
        <cite className="block mt-2 text-sm not-italic text-slate-500">
          — {block.metadata.source}
        </cite>
      )}
    </blockquote>
  );
});

/**
 * Code Block
 */
const CodeBlock = memo(function CodeBlock({ block }) {
  const language = block.metadata?.language || 'text';

  return (
    <div className="relative rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 text-xs text-slate-400">
        <span className="flex items-center gap-1.5">
          <Code className="h-3.5 w-3.5" />
          {language}
        </span>
      </div>
      <pre className="p-4 bg-slate-900 text-slate-100 overflow-x-auto text-sm font-mono">
        <code>{block.content}</code>
      </pre>
    </div>
  );
});

/**
 * Citation Block
 */
const CitationBlock = memo(function CitationBlock({ block, onCitationClick }) {
  const { sourceNumber, fileName, pageNumber, text } = block.metadata || {};

  return (
    <div
      className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
      onClick={() => onCitationClick?.({ sourceNumber, fileName, pageNumber })}
    >
      <div className="flex-shrink-0 w-6 h-6 rounded bg-amber-500 text-white flex items-center justify-center text-xs font-bold">
        {sourceNumber || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700 dark:text-neutral-300 line-clamp-2">
          {text || block.content}
        </p>
        {fileName && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <FileText className="h-3 w-3" />
            {fileName}
            {pageNumber && ` • Page ${pageNumber}`}
          </p>
        )}
      </div>
    </div>
  );
});

/**
 * File Reference Block
 */
const FileReferenceBlock = memo(function FileReferenceBlock({ block, onFileClick }) {
  const { fileName, fileType, fileSize, fileId } = block.metadata || {};

  return (
    <div
      className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-neutral-800 rounded-lg cursor-pointer hover:bg-slate-200 dark:hover:bg-neutral-700 transition-colors"
      onClick={() => onFileClick?.({ fileId, fileName })}
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
        <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700 dark:text-neutral-300 truncate">
          {fileName || 'Document'}
        </p>
        <p className="text-xs text-slate-500 dark:text-neutral-500">
          {fileType || 'File'} {fileSize && `• ${fileSize}`}
        </p>
      </div>
      <Link2 className="h-4 w-4 text-slate-400" />
    </div>
  );
});

/**
 * Table Block
 */
const TableBlock = memo(function TableBlock({ block }) {
  const { headers, rows } = block.metadata || { headers: [], rows: [] };

  if (!headers?.length) {
    return (
      <div className="text-sm text-slate-500 dark:text-neutral-500 italic">
        [Table: {block.content}]
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-neutral-700">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 dark:bg-neutral-800">
          <tr>
            {headers.map((header, idx) => (
              <th
                key={idx}
                className="px-4 py-2 text-left font-medium text-slate-700 dark:text-neutral-300 border-b border-slate-200 dark:border-neutral-700"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              className="border-b border-slate-100 dark:border-neutral-800 last:border-0"
            >
              {row.map((cell, cellIdx) => (
                <td key={cellIdx} className="px-4 py-2 text-slate-600 dark:text-neutral-400">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

/**
 * Image Block
 */
const ImageBlock = memo(function ImageBlock({ block }) {
  const { src, alt, caption } = block.metadata || {};

  return (
    <figure className="my-4">
      {src ? (
        <img src={src} alt={alt || block.content} className="rounded-lg max-w-full h-auto" />
      ) : (
        <div className="flex items-center justify-center h-40 bg-slate-100 dark:bg-neutral-800 rounded-lg">
          <Image className="h-8 w-8 text-slate-400" />
        </div>
      )}
      {caption && (
        <figcaption className="mt-2 text-center text-sm text-slate-500 dark:text-neutral-500">
          {caption}
        </figcaption>
      )}
    </figure>
  );
});

/**
 * Divider Block
 */
const DividerBlock = memo(function DividerBlock() {
  return <hr className="my-6 border-t border-slate-200 dark:border-neutral-700" />;
});

/**
 * Block Type to Component Map
 */
const BLOCK_COMPONENTS = {
  [BlockType.PARAGRAPH]: ParagraphBlock,
  [BlockType.HEADING]: HeadingBlock,
  [BlockType.BULLET]: BulletBlock,
  [BlockType.NUMBERED]: NumberedBlock,
  [BlockType.QUOTE]: QuoteBlock,
  [BlockType.CODE]: CodeBlock,
  [BlockType.CITATION]: CitationBlock,
  [BlockType.FILE_REFERENCE]: FileReferenceBlock,
  [BlockType.TABLE]: TableBlock,
  [BlockType.IMAGE]: ImageBlock,
  [BlockType.DIVIDER]: DividerBlock,
};

/**
 * BlockRenderer Component
 *
 * @param {Object} props
 * @param {Object} props.block - Block object to render
 * @param {boolean} props.isSelected - Whether block is selected
 * @param {Function} props.onSelect - Selection handler
 * @param {Function} props.onCitationClick - Citation click handler
 * @param {Function} props.onFileClick - File click handler
 */
const BlockRenderer = memo(function BlockRenderer({
  block,
  isSelected = false,
  onSelect,
  onCitationClick,
  onFileClick,
}) {
  const BlockComponent = useMemo(
    () => BLOCK_COMPONENTS[block.type] || ParagraphBlock,
    [block.type]
  );

  return (
    <BlockWrapper block={block} isSelected={isSelected} onSelect={onSelect}>
      <BlockComponent block={block} onCitationClick={onCitationClick} onFileClick={onFileClick} />
    </BlockWrapper>
  );
});

/**
 * BlockList Component - Renders multiple blocks
 */
export const BlockList = memo(function BlockList({
  blocks,
  selectedBlockId,
  onSelectBlock,
  onCitationClick,
  onFileClick,
}) {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {blocks.map(block => (
        <BlockRenderer
          key={block.id}
          block={block}
          isSelected={selectedBlockId === block.id}
          onSelect={onSelectBlock}
          onCitationClick={onCitationClick}
          onFileClick={onFileClick}
        />
      ))}
    </div>
  );
});

export default BlockRenderer;
